import React from 'react';
import { Link } from 'gatsby';
import { IconTypes } from '../Icon';
import { isDevEnvironment, mergeClassNames } from '../../../utils';
import translateEntryLink from '../../../utils/translateEntryLink';
import styles from './index.module.scss';

/**
 * LinkButton - link that visually appears as <PrimaryButton> or <SecondaryButton> without click handling
 * compatible with the Pwp Contentful button data
 *
 * @typedef {('Primary'|'Secondary')} LinkButtonTypes - Allowed button types
 * @typedef {('Link'|'Download'|'Modal'|'Play')} LinkButtonActions - Allowed button action types
 * @typedef {('Green'|'Purple'|'Blue'|'Black'|'White'|'Orange')} LinkButtonColors - Allowed link colors
 *
 * @param {object} param
 * @param {string} param.label Button label (title)
 * @param {string} [param.icon] if defined will set an icon and override type (only )
 * @param {LinkButtonTypes} param.type Button type
 * @param {LinkButtonActions} param.action 'Link'|'Download'|'Modal'|'Play'
 * @param {LinkButtonColors} [param.color] hex color
 * @param {object} [param.reference] internal entry reference
 * @param {object} [param.to] internal entry link
 * @param {object} [param.file] file link
 * @param {string} [param.url] external link
 * @param {string} [param.className] className to be passed to the wrapping <Link>|<a> element
 * @param {string} [param.internalClassName] className passed to the internal (visual) part of the link
 *
 */
export default function LinkButton({
  label,
  icon,
  type,
  action,
  color,
  url,
  reference,
  to,
  file,
  className,
  internalClassName,
}) {
  let link = reference ? translateEntryLink(reference) : to;
  let state = action === 'Modal' ? { modal: true } : undefined;
  let mergedClassNames = mergeClassNames(styles.link, className);

  let button = (
    <SwitchButtonAction action={action} type={type} iconName={icon} color={color} className={internalClassName}>
      {label}
    </SwitchButtonAction>
  );

  switch (true) {
    case Boolean(url):
      return (
        <p>
          <a href={url} className={mergedClassNames}>
            {button}
          </a>
        </p>
      );
    case Boolean(file):
      return (
        <p>
          <a href={file.file.url} className={mergedClassNames}>
            {button}
          </a>
        </p>
      );
    case Boolean(link):
      return (
        <p>
          <Link to={link} state={state} className={mergedClassNames}>
            {button}
          </Link>
        </p>
      );
    default:
      return isDevEnvironment ? <p>ButtonLink not fully defined, missing props</p> : null;
  }
}

/**
 * SwitchButtonAction
 * render out Primary|Secondary LinkButtonInternal based on type
 * TODO missing rendering out different appearances based on actions: 'Link'|'Download'|'Modal'|'Play'
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Link visible content
 * @param {LinkButtonActions} props.action 'Link'|'Download'|'Modal'|'Play'
 * @param {LinkButtonTypes} props.type Button type
 * @param {string} props.iconName icon name
 * @param {LinkButtonColors} props.color internal entry link
 * @param {string} [props.className] className being passed through to the
 * @returns JSX.Element
 */
function SwitchButtonAction({ children, action, type, iconName, color, className }) {
  switch (action) {
    case 'Modal':
    case 'Link':
      return (
        <SwitchLinkButton type={type} iconName={iconName} color={color} className={className}>
          {children}
        </SwitchLinkButton>
      );
    case 'Download':
    case 'Play':
      return <>{children}</>;
    default:
      return isDevEnvironment ? <p>Unknown action ${action}</p> : null;
  }
}

/**
 * SwitchLinkButton
 * render out Primary|Secondary LinkButtonInternal based on type
 * @param {object} param
 * @param {React.ReactNode} param.children Button label
 * @param {LinkButtonTypes} param.type Button type
 * @param {string} param.iconName icon name
 * @param {LinkButtonColors} param.color internal entry link
 * @param {string} [param.className] className being passed through to the
 * @returns JSX.Element
 */
function SwitchLinkButton({ children, type = types.Primary, iconName, color, className }) {
  switch (type) {
    case 'Primary':
      return (
        <PrimaryLinkButtonInternal iconType={IconTypes[iconName?.toLowerCase()]} color={color} className={className}>
          {children}
        </PrimaryLinkButtonInternal>
      );
    case 'Secondary':
      return (
        <SecondaryLinkButtonInternal color={color} className={className}>
          {children}
        </SecondaryLinkButtonInternal>
      );
    default:
      return <>children</>;
  }
}

/**
 * PrimaryLinkButtonInternal
 * Render out a span that visually looks like a <PrimaryButton>
 * @param {object} param
 * @param {React.ReactNode} param.children Link visible content
 * @param {LinkButtonColors} param.color internal entry link
 * @param {import('../Icon').IconType} param.iconType file link
 * @param {React.ReactNode} param.iconType icon
 * @param {string} [param.className] external link
 */
function PrimaryLinkButtonInternal({ children, color, iconType: IconType, className = '' }) {
  return (
    <span
      className={mergeClassNames(
        styles.linkButton,
        styles.primary,
        IconType ? styles.icon : '',
        styles[color || 'Default'],
        className
      )}
    >
      {IconType && <span className={styles.iconContainer}>{<IconType />}</span>}
      {children && <span className={styles.textContainer}>{children}</span>}
    </span>
  );
}

/**
 * SecondaryLinkButtonInternal
 * Render out a span that visually looks like a <SecondaryButton>
 * @param {object} param
 * @param {React.ReactNode} param.children Button label
 * @param {'White'|string} param.color internal entry link
 * @param {string} [param.className] external link
 */
function SecondaryLinkButtonInternal({ children, color, className = '' }) {
  /** @type {('default'|'light')} */
  const appearance = color === 'White' ? 'light' : 'default';
  return (
    <span className={mergeClassNames(styles.linkButton, styles.secondary, styles[appearance || 'default'], className)}>
      {children}
    </span>
  );
}

/** @type {{Primary:LinkButtonTypes, Secondary: LinkButtonTypes}} */
const types = {
  Primary: 'Primary',
  Secondary: 'Secondary',
};
